import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  CalendarIcon, 
  DocumentTextIcon, 
  CreditCardIcon,
  CogIcon,
  BuildingOfficeIcon,
  UserIcon,
  BellIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

export default function SettingsPage() {
  const { t } = useTranslation(['settings', 'common']);
  
  const settingsCategories = [
    {
      title: 'Account Settings',
      description: 'Edit your profile, change password, and manage security',
      icon: UserCircleIcon,
      link: '/settings/account',
      color: 'bg-cyan-500'
    },
    {
      title: 'Patient Settings',
      description: 'Configure patient form fields and intake process',
      icon: UserGroupIcon,
      link: '/patients/settings',
      color: 'bg-blue-500'
    },
    {
      title: 'Appointment Settings',
      description: 'Configure appointment types, durations, and scheduling',
      icon: CalendarIcon,
      link: '/appointments/settings',
      color: 'bg-green-500'
    },
    {
      title: 'Records Settings',
      description: 'Configure medical records and documentation',
      icon: DocumentTextIcon,
      link: '/records/settings',
      color: 'bg-purple-500'
    },
    {
      title: 'Billing Settings',
      description: 'Configure billing, payments, and insurance',
      icon: CreditCardIcon,
      link: '/billing/settings',
      color: 'bg-yellow-500'
    },
    {
      title: 'Practice Settings',
      description: 'Configure practice information and preferences',
      icon: BuildingOfficeIcon,
      link: '/practice/settings',
      color: 'bg-red-500'
    },
    {
      title: 'User Settings',
      description: 'Manage user accounts and permissions',
      icon: UserIcon,
      link: '/users/settings',
      color: 'bg-indigo-500'
    },
    {
      title: 'Notifications',
      description: 'Configure notification preferences and alerts',
      icon: BellIcon,
      link: '/notifications/settings',
      color: 'bg-pink-500'
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide settings and integrations',
      icon: CogIcon,
      link: '/system/settings',
      color: 'bg-gray-500'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Settings - Doctif</title>
        <meta name="description" content="Practice settings and configuration" />
      </Helmet>
      
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-card-foreground mb-2">
            Settings
          </h1>
          <p className="text-muted-foreground">
            Configure practice settings, user preferences, and system options
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {settingsCategories.map((category, index) => {
            const IconComponent = category.icon;
            return (
              <Link
                key={index}
                to={category.link}
                className="bg-card rounded-xl border border-border shadow-sm hover:shadow-md transition-all duration-200 hover:border-primary/30 group"
              >
                <div className="p-6">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className={`p-3 rounded-xl ${category.color} bg-opacity-10 group-hover:bg-opacity-20 transition-colors`}>
                      <IconComponent className={`h-6 w-6 text-${category.color.split('-')[1]}-600`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-card-foreground group-hover:text-primary transition-colors">
                        {category.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {category.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mt-12 bg-card rounded-xl border border-border shadow-sm">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-card-foreground mb-4">{t('quickActions.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/patients/settings"
                className="btn-primary text-center"
              >
                Configure Patient Forms
              </Link>
              <Link
                to="/practice/settings"
                className="btn-secondary text-center"
              >
                Practice Information
              </Link>
              <Link
                to="/users/settings"
                className="btn-secondary text-center"
              >
                Manage Users
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
